import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { Lang } from './translations';

// Not routed through moment's own locale() — that is a GLOBAL setting on the
// shared moment instance, and this app also uses moment for things that must
// never change shape with the language toggle (e.g. parsing 'YYYY-MM-DD' order
// dates). A small local name table keeps the toggle scoped to display only.
const MONTH_NAMES: Record<Lang, string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  no: ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember']
};

export function formatMonth(value: string, lang: Lang): string {
  const m = moment(value, 'YYYY-MM');
  const names = MONTH_NAMES[lang] || MONTH_NAMES.en;
  return names[m.month()] + ' ' + m.format('YYYY');
}

const WEEKDAY_NAMES: Record<Lang, string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  no: ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør']
};

// Formats a moment as "Wed 20.08.2025" / "ons 20.08.2025" — the weekday
// abbreviation swapped in by hand for the same reason formatMonth is: no
// global moment.locale() call, so parsing elsewhere in the app is untouched.
export function formatWeekdayDate(m: moment.Moment, lang: Lang): string {
  const names = WEEKDAY_NAMES[lang] || WEEKDAY_NAMES.en;
  return names[m.day()] + ' ' + m.format('DD.MM.YYYY');
}

// The month options for every period picker: the current month and the previous
// 11. The portal is an operational tool, not an archive — anything older is a
// billing question.
//
// Call this ONCE into a field. Never expose it through a getter bound to
// *ngFor: a getter returns freshly minted objects on every call, so ngFor's
// identity tracking sees 12 brand-new items on every change-detection pass and
// rebuilds all of them. With <mat-option> each rebuild re-registers with the
// parent <mat-select> and schedules another pass, which locks up the tab.
//
// Only `value` is stable data — the display label is computed fresh per
// render (see formatMonth) so it can react to the language toggle without the
// array itself needing to change.
export function buildMonths(): { value: string }[] {
  const out: { value: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const m = moment().subtract(i, 'months');
    out.push({ value: m.format('YYYY-MM') });
  }
  return out;
}
